/* @flow */
import * as React from 'react';
import idx from 'idx';
import { isEqual, minBy, memoize } from 'lodash';
import { graphql, compose } from 'react-apollo';
import update from 'immutability-helper';

import { promiseRelationship } from 'chat_v2/utils/ChatPromises';
import { withRouter } from 'app/context/Router';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import ErrorReporter from 'app/components/ErrorReporter';
import RemindAPI from 'lib/RemindAPI';
import StreamSectionListItem from './StreamSectionListItem';
import Subscriber from 'entities/Subscriber';
import TriggerComposerButton from 'chat_v2/components/TriggerComposerButton';
import STREAM_SECTION_CHATS_QUERY from 'chat/components/StreamSectionList/queries/StreamSectionChatsQuery.gql';
import ENTITY_CHAT_QUERY from 'chat/components/StreamSectionList/queries/StreamSectionListChatsEntityStreamQuery.gql';

import type { StreamSectionChats } from 'chat/components/StreamSectionList/queries/__generated__/StreamSectionChats';
import type { StreamSectionListChatsEntityStream } from 'chat/components/StreamSectionList/queries/__generated__/StreamSectionListChatsEntityStream';

type Props = {
  chatStreamData: ReactApolloData<StreamSectionChats>,
  chatStreamUuids: string[],
  entityChatUuids: string[],
  groupCode: string,
  groupId: number,
  isScrolledToBottom: boolean,
  newChatMemberId: ?string,
  router: *,
  searchQuery: string,
};

type State = {
  newChatUser: ?Subscriber,
  entityChatData: ?ReactApolloData<StreamSectionListChatsEntityStream>,
};

class StreamSectionListChats extends React.Component<Props, State> {
  state = {
    newChatUser: null,
    entityChatData: null,
  };

  componentDidMount() {
    const { newChatMemberId, entityChatUuids } = this.props;
    const { chatStreams } = this.props.chatStreamData;
    const oldestDate = this.getOldestDate(chatStreams);

    if (chatStreams && this.props.isScrolledToBottom) {
      this.loadMore(oldestDate);
    }

    if (entityChatUuids.length) {
      this.setEntityChats(this.props.entityChatUuids);
    }

    if (newChatMemberId) {
      this.setNewChatUser(newChatMemberId);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { newChatMemberId } = this.props;
    const { chatStreams } = this.props.chatStreamData;
    const oldestDate = this.getOldestDate(chatStreams);

    if (prevProps.chatStreamUuids[0] !== this.props.chatStreamUuids[0]) {
      this.props.chatStreamData.refetch();
    }

    if (prevProps.entityChatUuids[0] !== this.props.entityChatUuids[0]) {
      this.setEntityChats(this.props.entityChatUuids);
    }

    if (!chatStreams) {
      return;
    }

    if (newChatMemberId && prevProps.newChatMemberId !== newChatMemberId) {
      this.setNewChatUser(newChatMemberId);
    }

    if (prevProps.searchQuery !== this.props.searchQuery) {
      this.loadQuery(this.props.searchQuery);
    }

    if (this.props.searchQuery) {
      // don't ever call loadMore if a search query exists
      return;
    }

    if (
      (!prevProps.isScrolledToBottom && this.props.isScrolledToBottom) ||
      (!prevProps.chatStreamData.chatStreams && this.props.isScrolledToBottom)
    ) {
      this.loadMore(oldestDate);
    }
  }

  getOldestDate = (chatStreams: *) => {
    const dates = chatStreams ? chatStreams.map(s => s.updatedAt) : [];
    return minBy(dates);
  };

  async setEntityChats(entityChatUuids: string[]) {
    const entityChatData = await RemindAPI.execute({
      query: ENTITY_CHAT_QUERY,
      variables: {
        uuids: entityChatUuids,
      },
      forceFetch: true,
    });
    this.setState({ entityChatData });
  }

  setNewChatUser = (memberId: ?string) => {
    if (!memberId) {
      this.setState({ newChatUser: null });
    }
    return promiseRelationship(parseInt(memberId, 10)).then(relationship => {
      const newChatUser = relationship.getRelatedUser()
        ? relationship.getRelatedUser().toJSON()
        : null;
      this.setState({ newChatUser });
    });
  };

  closeNewChat = (event: SyntheticEvent<>) => {
    event.preventDefault();
    this.setState({ newChatUser: null });
    this.props.router.push({ pathname: `/classes/${this.props.groupCode}/messages/` });
  };

  addStreamsToQueryResult(result: *, newStreams: *) {
    return update(result, {
      chatStreams: { $set: [...result.chatStreams, ...newStreams.chatStreams] },
    });
  }

  replaceStreamswithQueryResult(result: *, newStreams: *) {
    return update(result, {
      chatStreams: { $set: [...newStreams.chatStreams] },
    });
  }

  loadQuery = (searchQuery: string) => {
    this.props.chatStreamData.fetchMore({
      variables: {
        chatQuery: searchQuery,
        chatUuids: null,
      },
      updateQuery: (previous, { fetchMoreResult }) => {
        this.loadMore.cache.clear();
        if (!fetchMoreResult) {
          return previous;
        }
        return this.replaceStreamswithQueryResult(previous, fetchMoreResult);
      },
    });
  };

  loadMore = memoize<Array<string>, void>((maxTimestamp: ?string) => {
    if (!maxTimestamp) {
      return;
    }

    // loadMore will continue fetching until it returns an empty
    // `fetchMoreResult`. The empty result will be memoized so
    // no more fetches will happen with the same maxTimestamp
    this.props.chatStreamData.fetchMore({
      variables: {
        maxTimestamp,
        chatUuids: null,
      },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previous;
        }

        // when a user sends an announcement to a group, the `maxTimestamp`
        // of the list of chats will change. memoization of this function is
        // based on `maxTimestamp`, so we need to manually ensure that `loadMore`
        // does not load a duplicative list of chats
        const prevUuids = previous ? previous.chatStreams.map(stream => stream.uuid).sort() : [];
        const newUuids = fetchMoreResult.chatStreams.map(stream => stream.uuid).sort();
        if (isEqual(prevUuids, newUuids)) {
          return fetchMoreResult;
        }

        // if there are truly additional distinct streams to load,
        // then add them to the list
        return this.addStreamsToQueryResult(previous, fetchMoreResult);
      },
    });
  });

  renderEmpty() {
    return <TriggerComposerButton label="Send a message" level="secondary" full />;
  }

  renderLoading() {
    return (
      <li className="ChatList-loading">
        <CenteredLoadingIndicator />
      </li>
    );
  }

  renderNewChat(newChatUser: *) {
    return (
      <StreamSectionListItem
        newChat
        newChatOnClose={this.closeNewChat}
        url={`/classes/${this.props.groupCode}/messages/new/${newChatUser.id}`}
        otherMember={newChatUser}
        chatType="individual"
        key={newChatUser.uuid}
        name={newChatUser.name}
      />
    );
  }

  renderChatStreams() {
    const { chatStreamData, searchQuery } = this.props;
    const { chatStreams, loading } = chatStreamData;
    if (!chatStreams && !loading) {
      return <ErrorReporter error="failed to load chatStreams in StreamSectionListChats" />;
    }

    if (loading && searchQuery) {
      return this.renderLoading();
    }

    if (chatStreams && !chatStreams.length && !searchQuery) {
      return this.renderEmpty();
    }

    if (chatStreams && chatStreams.length) {
      return (
        <React.Fragment>
          {chatStreams.map(stream => {
            const chatType = stream.membershipsCount > 2 ? 'group' : 'individual';
            return (
              <StreamSectionListItem
                otherMember={stream.otherMember}
                chatType={chatType}
                key={stream.uuid}
                streamUuid={stream.uuid}
                name={stream.title}
                date={stream.preview ? stream.preview.sentAt : stream.updatedAt}
                preview={stream.preview ? stream.preview.preview : ''}
                url={`/classes/${this.props.groupCode}/messages/${stream.uuid}`}
              />
            );
          })}
          {loading ? this.renderLoading() : null}
        </React.Fragment>
      );
    }
    return null;
  }

  renderEntityChat() {
    const { searchQuery } = this.props;
    const { entityChatData } = this.state;
    if (entityChatData && !entityChatData.streams && !entityChatData.loading) {
      return <ErrorReporter error="failed to load entity chat in StreamSectionListChats" />;
    }

    const stream = idx(entityChatData, _ => _.streams[0]);
    if (!stream) {
      return null;
    }

    if (stream.__typename !== 'EntityStream') {
      return (
        <ErrorReporter error="loaded a ChatStream when attempting to render an EntityStream" />
      );
    }

    const searchQueryMatchesStream = stream.name.toLowerCase().includes(searchQuery.toLowerCase());
    if (searchQuery && !searchQueryMatchesStream) {
      return null;
    }

    return (
      <StreamSectionListItem
        groupAvatar={stream.avatarUrl}
        key={stream.uuid}
        streamUuid={stream.uuid}
        name={stream.name}
        date={stream.preview ? stream.preview.sentAt : ''}
        preview={stream.preview ? stream.preview.preview : ''}
        url={`/classes/${this.props.groupCode}/messages/${stream.uuid}`}
      />
    );
  }

  render() {
    const { chatStreamData } = this.props;
    const { newChatUser, entityChatData } = this.state;

    return (
      <React.Fragment>
        {newChatUser ? this.renderNewChat(newChatUser) : null}
        {entityChatData && !entityChatData.loading ? this.renderEntityChat() : null}
        {chatStreamData ? this.renderChatStreams() : null}
      </React.Fragment>
    );
  }
}

const withChatStreams = graphql(STREAM_SECTION_CHATS_QUERY, {
  options: ({ groupId }) => ({
    variables: {
      groupId,
    },
    notifyOnNetworkStatusChange: true,
  }),
  props: ({ data }) => ({
    chatStreamData: data,
  }),
});

export { StreamSectionListChats };

export default compose(withChatStreams, withRouter)(StreamSectionListChats);
